import type { RouteLocationNormalizedGeneric } from '#vue-router'
import type { AssessmentTemplatesPaginatedData } from '../../model/assessments/interfaces'
import type { ApiCallbacks, ApiResponse } from '@shared/api'

import { getAxiosErrorMessage } from '@shared/api'
import { pick } from '@shared/lib/collections'
import { MAX_PER_PAGE, toPaginationValue, useAdminRoutes } from '@shared/lib/routes'
import { useToast } from '@shared/ui/toast'

import { useAssessmentTemplatesStore } from '../../model/assessment-templates/store'

interface AssessmentTemplatesApiOptions extends ApiCallbacks<AssessmentTemplatesPaginatedData> {
  route?: RouteLocationNormalizedGeneric
}

/**
 * `GET /admin-api/assessment-templates` — the index (contract §1). Rows carry counts but no
 * `data`, so nothing needs normalising; fetch the document with `useAssessmentTemplateShowApi`.
 */
export const useAssessmentTemplatesApi = async (
  options?: AssessmentTemplatesApiOptions,
): Promise<AssessmentTemplatesPaginatedData | null> => {
  const { $axios } = useNuxtApp()
  const toast = useToast()
  const route = options?.route || useRoute()
  const store = useAssessmentTemplatesStore()

  const url = useAdminRoutes('admin.assessment-templates.index', pick({
    search: store.getFilterSearch,
    type: store.getFilterType,
    discipline_code: store.getFilterDisciplineCode,
    grade: store.getFilterGrade,
    status: store.getFilterStatus,
    per_page: toPaginationValue(route.query?.per_page, store.getPerPage, MAX_PER_PAGE),
    page: toPaginationValue(route.query?.page, store.getCurrentPage),
  }))

  options?.onStart?.()
  store.setLoading(true)

  try {
    if (!url) {
      const msg = 'Unexpected error. Invalid request URL.'
      toast.error(msg)
      options?.onError?.(msg)
      return null
    }

    const { data } = await $axios.get<ApiResponse>(url)

    if (!data.success) {
      toast.error(data.message)
      options?.onError?.(data.message)
      return null
    }

    const paginatedData: AssessmentTemplatesPaginatedData | null = data.data || null
    store.setTemplatesPaginatedData(paginatedData)
    options?.onSuccess?.(paginatedData)
    return paginatedData
  } catch (err: unknown) {
    const msg = getAxiosErrorMessage(err)
    toast.error(msg)
    options?.onError?.(msg)
    return null
  } finally {
    store.setLoading(false)
    options?.onFinish?.()
  }
}
